// Lightweight GA4 + Meta Pixel event helper.
// Tags are injected from __root.tsx but only become active after consent
// (see consent.ts). All calls are no-ops on the server or before consent.

import { hasConsent } from "./consent";

type GtagFn = (...args: unknown[]) => void;
type FbqFn = ((...args: unknown[]) => void) & { callMethod?: (...a: unknown[]) => void; queue?: unknown[]; loaded?: boolean; version?: string };

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: GtagFn;
    fbq?: FbqFn;
    _fbq?: FbqFn;
  }
}

function gtag(...args: unknown[]) {
  if (typeof window === "undefined") return;
  if (!hasConsent()) return;
  if (typeof window.gtag === "function") {
    window.gtag(...args);
  } else {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(args);
  }
}

function fbq(...args: unknown[]) {
  if (typeof window === "undefined") return;
  if (!hasConsent()) return;
  if (typeof window.fbq === "function") {
    (window.fbq as (...a: unknown[]) => void)(...args);
  }
}

type Item = {
  slug: string;
  name: string;
  price: number;
  category?: string;
  quantity?: number;
};

function toGaItem(i: Item) {
  return {
    item_id: i.slug,
    item_name: i.name,
    price: i.price,
    item_category: i.category,
    quantity: i.quantity ?? 1,
  };
}

export const analytics = {
  pageView(path: string, title?: string) {
    gtag("event", "page_view", {
      page_path: path,
      page_title: title,
      page_location: typeof window !== "undefined" ? window.location.href : undefined,
    });
    fbq("track", "PageView");
  },
  viewItem(item: Item) {
    gtag("event", "view_item", {
      currency: "USD",
      value: item.price,
      items: [toGaItem(item)],
    });
    fbq("track", "ViewContent", {
      content_ids: [item.slug],
      content_name: item.name,
      content_type: "product",
      content_category: item.category,
      value: item.price,
      currency: "USD",
    });
  },
  addToCart(item: Item) {
    const qty = item.quantity ?? 1;
    const value = item.price * qty;
    gtag("event", "add_to_cart", {
      currency: "USD",
      value,
      items: [toGaItem(item)],
    });
    fbq("track", "AddToCart", {
      content_ids: [item.slug],
      content_name: item.name,
      content_type: "product",
      value,
      currency: "USD",
    });
  },
  removeFromCart(item: Item) {
    gtag("event", "remove_from_cart", {
      currency: "USD",
      value: item.price * (item.quantity ?? 1),
      items: [toGaItem(item)],
    });
  },
  beginCheckout(items: Item[], value: number) {
    gtag("event", "begin_checkout", {
      currency: "USD",
      value,
      items: items.map(toGaItem),
    });
    fbq("track", "InitiateCheckout", {
      content_ids: items.map((i) => i.slug),
      contents: items.map((i) => ({ id: i.slug, quantity: i.quantity ?? 1 })),
      num_items: items.reduce((s, i) => s + (i.quantity ?? 1), 0),
      value,
      currency: "USD",
    });
  },
  purchase(transactionId: string, items: Item[], value: number) {
    gtag("event", "purchase", {
      transaction_id: transactionId,
      currency: "USD",
      value,
      items: items.map(toGaItem),
    });
    fbq("track", "Purchase", {
      content_ids: items.map((i) => i.slug),
      contents: items.map((i) => ({ id: i.slug, quantity: i.quantity ?? 1 })),
      value,
      currency: "USD",
    });
  },
  search(term: string) {
    gtag("event", "search", { search_term: term });
    fbq("track", "Search", { search_string: term });
  },
};
